import { ActionBar, Button, Input } from "@core/components";
import { icons } from "@core/assets";
import {
  useAddAnnotationNote,
  useGetAnnotationNotes,
} from "@modules/docs/annotations/hooks";
import { useTranslate } from "@core/hooks";
import NoteCard from "./NoteCard";
import { useState } from "react";

type Props = {
  onClose: VoidFunction;
  annotationId: string | null;
  readOnly?: boolean;
};

const AnnotationComment = ({ onClose, annotationId, readOnly = false }: Props) => {
  const { t } = useTranslate("documents");
  const { data, isLoading, isError } = useGetAnnotationNotes(
    annotationId ?? "",
  );
  const addNoteAnnotation = useAddAnnotationNote(annotationId ?? "");
  const notes = data ?? [];

  const [content, setContent] = useState("");

  const handleAddNote = () => {
    const trimmed = content.trim();
    if (!trimmed || !annotationId) return;

    addNoteAnnotation.mutate(trimmed, {
      onSuccess: () => {
        setContent("");
      },
    });
  };

  return (
    <ActionBar
      title={t("annotations.panel-title")}
      minWidth="34rem"
      editable={false}
      saveAs="button"
      onClose={onClose}
    >
      <div className="w-full h-full flex flex-col gap-4">
        {/* Notes list */}
        <div className="w-full flex-1 overflow-y-auto flex flex-col gap-3">
          {isLoading && (
            <div className="text-sm text-secondary-500">
              {t("annotations.loading")}
            </div>
          )}

          {isError && !isLoading && (
            <div className="text-sm text-danger-500">
              {t("annotations.load-error")}
            </div>
          )}

          {!isLoading && !isError && notes?.length === 0 && (
            <div className="text-sm text-secondary-500">
              {t("annotations.empty")}
            </div>
          )}

          {!isLoading &&
            !isError &&
            notes?.map((note) => (
              <NoteCard
                key={note.id}
                note={note}
                annotationId={annotationId ?? ""}
                canDelete={!readOnly}
              />
            ))}
        </div>

        {/* Add note input — hidden when read-only (approved / rejected) */}
        {!readOnly && (
          <div className="w-full h-[10%] flex items-end justify-center gap-2 pb-2">
            <Input
              placeholder={t("annotations.add-placeholder")}
              className="w-[95%] h-[3.2rem]"
              value={content}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setContent(e.target.value)
              }
              onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  handleAddNote();
                }
              }}
              disabled={!annotationId}
            />

            <Button
              iconOnly
              leftIcon={icons["cursor"]}
              iconStrokeWidth={1.6}
              size="lg"
              className="w-[4rem]"
              disabled={!annotationId || content.trim() === "" || addNoteAnnotation.isPending}
              isLoading={addNoteAnnotation.isPending}
              onClick={handleAddNote}
            />
          </div>
        )}
      </div>
    </ActionBar>
  );
};

export default AnnotationComment;
